import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  BasketValidation,
  CreditTermsInput,
  InvoiceDetail,
  InvoiceListItem,
  InvoiceStatus,
  InvoiceVerificationLink,
  OpticalJobInput,
  VerifiedOrderPaymentStatus,
  PaginatedResult,
  PaymentMethod,
  SalesOrderDetail,
  SalesOrderStatus,
  SalesOrderSummary,
  SalesSettingsSummary,
  TaxProfileSummary,
} from '@optiflow/types';
import { api } from '@/services/apiClient';
import { useIdempotentMutation } from '@/services/useIdempotentMutation';
import { inventoryKeys } from '@/modules/inventory/api/inventory.api';

export interface SalesOrderListParams {
  page?: number;
  pageSize?: number;
  status?: SalesOrderStatus | '';
  search?: string;
}

export interface CounterSaleLine {
  /** The client's key for the line, unique in the request; an optical job names its lines by it. */
  lineKey?: string;
  variantId: string;
  quantity: number;
  discountMinor?: number;
  taxProfileId?: string;
  /** The opaque token a frame recommendation signed, for a frame sold from it. Checkout verifies it; the line names no price and nothing else about the recommendation. */
  recommendationToken?: string;
}

export interface Tender {
  method: PaymentMethod;
  amountMinor: number;
  reference?: string;
}

export const salesKeys = {
  all: ['sales'] as const,
  orders: (params: SalesOrderListParams) => [...salesKeys.all, 'orders', params] as const,
  order: (id: string) => [...salesKeys.all, 'order', id] as const,
  invoice: (id: string) => [...salesKeys.all, 'invoice', id] as const,
  invoices: () => [...salesKeys.all, 'invoices'] as const,
  taxProfiles: () => [...salesKeys.all, 'tax-profiles'] as const,
  settings: () => [...salesKeys.all, 'settings'] as const,
};

export function useSalesOrdersQuery(params: SalesOrderListParams) {
  return useQuery({
    queryKey: salesKeys.orders(params),
    queryFn: () =>
      api.get<PaginatedResult<SalesOrderSummary>>('/sales/orders', {
        params: {
          page: params.page ?? 1,
          pageSize: params.pageSize ?? 20,
          ...(params.status ? { status: params.status } : {}),
          ...(params.search?.trim() ? { search: params.search.trim() } : {}),
        },
      }),
    placeholderData: (previous) => previous,
  });
}

export function useSalesOrderQuery(id: string | undefined) {
  return useQuery({
    queryKey: salesKeys.order(id ?? ''),
    queryFn: () => api.get<SalesOrderDetail>(`/sales/orders/${id ?? ''}`),
    enabled: Boolean(id),
  });
}

export function useInvoiceQuery(id: string | undefined) {
  return useQuery({
    queryKey: salesKeys.invoice(id ?? ''),
    queryFn: () => api.get<InvoiceDetail>(`/sales/invoices/${id ?? ''}`),
    enabled: Boolean(id),
  });
}

export interface InvoiceListParams {
  page: number;
  pageSize: number;
  search: string;
  branchId: string;
  status: InvoiceStatus | '';
  paymentStatus: VerifiedOrderPaymentStatus | '';
  dateFrom: string;
  dateTo: string;
}

/** Invoices of the branches the caller can reach; every filter is applied by the server, never in the browser. */
export function useInvoicesQuery(params: InvoiceListParams) {
  return useQuery({
    queryKey: [...salesKeys.invoices(), params] as const,
    queryFn: () =>
      api.get<PaginatedResult<InvoiceListItem>>('/sales/invoices', {
        params: {
          page: params.page,
          pageSize: params.pageSize,
          ...(params.search.trim() ? { search: params.search.trim() } : {}),
          ...(params.branchId ? { branchId: params.branchId } : {}),
          ...(params.status ? { status: params.status } : {}),
          ...(params.paymentStatus ? { paymentStatus: params.paymentStatus } : {}),
          ...(params.dateFrom ? { dateFrom: params.dateFrom } : {}),
          ...(params.dateTo ? { dateTo: params.dateTo } : {}),
        },
      }),
    placeholderData: (previous) => previous,
  });
}

/** The invoice's own verification page, read only when someone asks to open it, so its link never sits in a page payload. */
export function fetchInvoiceVerification(id: string) {
  return api.get<InvoiceVerificationLink>(`/sales/invoices/${id}/verification`);
}

/** Tax profiles change rarely and every basket needs them, so they are cached for the session. */
export function useTaxProfilesQuery() {
  return useQuery({
    queryKey: salesKeys.taxProfiles(),
    queryFn: () => api.get<TaxProfileSummary[]>('/sales/tax-profiles'),
    staleTime: 5 * 60_000,
  });
}

export function useSalesSettingsQuery() {
  return useQuery({
    queryKey: salesKeys.settings(),
    queryFn: () => api.get<SalesSettingsSummary>('/sales/settings'),
    staleTime: 5 * 60_000,
  });
}

/** A sale changes stock as well as orders, so both caches are invalidated. */
function useSaleInvalidation() {
  const queryClient = useQueryClient();

  return () => {
    void queryClient.invalidateQueries({ queryKey: salesKeys.all });
    void queryClient.invalidateQueries({ queryKey: inventoryKeys.all });
  };
}

export interface CounterSalePayload {
  customerId?: string;
  lines: CounterSaleLine[];
  /** Pairs of spectacles, each naming its frame and lens lines by key. Needs optical.create. */
  opticalJobs?: OpticalJobInput[];
  tenders?: Tender[];
  credit?: CreditTermsInput;
  notes?: string;
}

export function useCreateCounterSaleMutation() {
  const invalidate = useSaleInvalidation();

  return useIdempotentMutation<SalesOrderDetail, CounterSalePayload>(
    (payload, config) => api.post<SalesOrderDetail>('/sales/orders', payload, config),
    { onSuccess: invalidate },
  );
}

export function useRecordTendersMutation(orderId: string) {
  const invalidate = useSaleInvalidation();

  return useIdempotentMutation<{ orderId: string; recorded: number }, Tender[]>(
    (tenders, config) =>
      api.post<{ orderId: string; recorded: number }>(
        `/sales/orders/${orderId}/payments`,
        { tenders },
        config,
      ),
    { onSuccess: invalidate },
  );
}

/**
 * A price and availability check.
 *
 * Not a reservation, and not cached: the whole point is to ask the server what is true *now*, so a
 * stale answer would defeat it.
 */
export interface ValidateBasketPayload {
  /** `lineKey` comes back unchanged on the line's result, so results are matched to lines by key, never by position. */
  lines: {
    variantId: string;
    quantity: number;
    expectedUnitPriceMinor?: number;
    lineKey?: string;
  }[];
  customerId?: string;
  taxProfileId?: string;
  financedMinor?: number;
  /** Pairs in the checkout's own shape: held to its pair rules, answered in `optical`. Needs optical.create. */
  opticalJobs?: OpticalJobInput[];
}

export function validateBasket(payload: ValidateBasketPayload): Promise<BasketValidation> {
  return api.post<BasketValidation>('/sales/orders/validate', payload);
}

export function useValidateBasketMutation() {
  return useMutation({ mutationFn: validateBasket });
}
